// apps/web/src/components/layout/SplitPane.tsx
//
// Phase 8 D-06/D-07/D-08 — resizable split pane following the W3C ARIA Window
// Splitter pattern (w3.org/WAI/ARIA/apg/patterns/windowsplitter).
// Uses CSS Grid `grid-template-columns: 1fr 8px ${rightPercent}%` — no
// react-resizable-panels dependency.
//
// The inline `style` carrying `gridTemplateColumns` is the documented
// UI-SPEC §"Sanitization & Security Contract" (line 863) exception — the
// only Phase 8 inline-style carve-out. It is allowlisted in
// scripts/sanitize/inline-style-allowlist.txt. The template value is
// exclusively `1fr 8px ${rightPercent}%` where rightPercent is the
// numeric ratio clamped to [minRatio*100, maxRatio*100] — no user-string
// interpolation flows into the CSS (Threat T-08-06-01 mitigation).
//
// localStorage persistence is per-user UI preference (REND-03 pattern).
// Reads/writes are guarded for SSR (Phase 13 forward-compat) and silently
// fall back to defaultRatio on parse/range failure (Threat T-08-06-02).
import { useEffect, useRef, useState } from "react";

export interface SplitPaneProps {
  storageKey: string;
  defaultRatio: number;
  minRatio: number;
  maxRatio: number;
  ariaControls: string;
  leftChildren: React.ReactNode;
  rightChildren: React.ReactNode;
}

const STEP = 0.04; // 4% per arrow key press per UI-SPEC §"Resizable split-pane sizing"

function readStoredRatio(
  storageKey: string,
  minRatio: number,
  maxRatio: number,
  defaultRatio: number,
): number {
  // SSR / no-window guard: localStorage is browser-only.
  if (typeof window === "undefined" || !window.localStorage) {
    return defaultRatio;
  }
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored === null) {
      return defaultRatio;
    }
    const parsed = Number.parseFloat(stored);
    if (!Number.isFinite(parsed)) {
      return defaultRatio;
    }
    // Clamp out-of-range stored values to the bounds (defense in depth).
    if (parsed < minRatio) return minRatio;
    if (parsed > maxRatio) return maxRatio;
    return parsed;
  } catch {
    // Storage access can throw (e.g. Safari private mode, disabled cookies).
    return defaultRatio;
  }
}

function writeStoredRatio(storageKey: string, ratio: number): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(storageKey, String(ratio));
  } catch {
    // Quota exceeded / disabled — silently drop. UI behavior unaffected.
  }
}

export function SplitPane(props: SplitPaneProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState<number>(() =>
    readStoredRatio(props.storageKey, props.minRatio, props.maxRatio, props.defaultRatio),
  );

  // WR-08 — debounce the localStorage write. Holding ArrowRight at typical
  // key-repeat rate would otherwise write ~30× per second, hammering disk
  // and firing a `storage` event per write. 250ms after the last ratio
  // change is long enough to capture the user's intent without flooding.
  useEffect(() => {
    const id = setTimeout(() => writeStoredRatio(props.storageKey, ratio), 250);
    return () => clearTimeout(id);
  }, [ratio, props.storageKey]);

  function clamp(r: number): number {
    return Math.max(props.minRatio, Math.min(props.maxRatio, r));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    switch (e.key) {
      case "ArrowLeft":
        setRatio((r) => clamp(r - STEP));
        e.preventDefault();
        break;
      case "ArrowRight":
        setRatio((r) => clamp(r + STEP));
        e.preventDefault();
        break;
      case "Home":
        setRatio(props.minRatio);
        e.preventDefault();
        break;
      case "End":
        setRatio(props.maxRatio);
        e.preventDefault();
        break;
      default:
        return;
    }
  }

  // CR-03 fix — pointer-drag handler with three corrections vs the previous
  // implementation:
  //   (1) setPointerCapture on the divider element so pointermove/pointerup
  //       reliably arrive even when the cursor escapes the element (W3C
  //       Window Splitter pattern requirement).
  //   (2) Guard against double-attach. If a previous drag's listener was
  //       still on window (e.g., pointerup fired outside the viewport and
  //       the cleanup raced a new pointerdown), the existing-handler ref
  //       fires first to drop those before installing new ones.
  //   (3) Guard against div-by-zero on a zero-width container (jsdom-like
  //       test environments).
  //   The window-level pointermove/pointerup listeners are PRESERVED so
  //   the existing browser-mode smoke test (which dispatches PointerEvent
  //   on window) continues to pass; setPointerCapture is additive and
  //   does not change the listener targets.
  const activeDragRef = useRef<{
    onMove: (ev: PointerEvent) => void;
    onUp: () => void;
  } | null>(null);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    if (containerRef.current === null) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    if (containerWidth === 0) return; // jsdom / unmeasurable container — bail.
    const startX = e.clientX;
    const startRatio = ratio;
    const target = e.currentTarget;
    const pointerId = e.pointerId;

    // (1) Capture the pointer so subsequent pointermove/pointerup fire on
    // this element even if the cursor leaves it. setPointerCapture is a
    // no-op if the browser doesn't support it (older test envs), but every
    // modern browser + Playwright supports it.
    if (typeof target.setPointerCapture === "function") {
      try {
        target.setPointerCapture(pointerId);
      } catch {
        // Some test envs throw on setPointerCapture for synthetic events —
        // ignore; the window-listener path still works.
      }
    }

    // (2) If a previous drag's listeners are still attached (cleanup raced
    // a new pointerdown), drop them before installing new ones.
    if (activeDragRef.current !== null) {
      window.removeEventListener("pointermove", activeDragRef.current.onMove);
      window.removeEventListener("pointerup", activeDragRef.current.onUp);
      activeDragRef.current = null;
    }

    const onPointerMove = (ev: PointerEvent): void => {
      const dx = ev.clientX - startX;
      // Use functional setter form for resilience under concurrent updates;
      // baseline ratio is captured once at pointerdown via startRatio.
      setRatio(() => clamp(startRatio + dx / containerWidth));
    };
    const onPointerUp = (): void => {
      if (typeof target.releasePointerCapture === "function") {
        try {
          target.releasePointerCapture(pointerId);
        } catch {
          // Capture may have never been granted; ignore.
        }
      }
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      activeDragRef.current = null;
    };
    activeDragRef.current = { onMove: onPointerMove, onUp: onPointerUp };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }

  const rightPercent = (1 - ratio) * 100;

  return (
    <div
      ref={containerRef}
      className="grid h-full"
      // INLINE STYLE EXCEPTION — UI-SPEC §"Sanitization & Security Contract"
      // line 863. SplitPane is the ONLY Phase 8 file allowed to ship a
      // `style={{}}`; allowlisted in scripts/sanitize/inline-style-allowlist.txt.
      // The template carries only `1fr 8px ${number}%` — no user-string flows in.
      style={{ gridTemplateColumns: `1fr 8px ${rightPercent}%` }}
    >
      <div className="overflow-auto p-4">{props.leftChildren}</div>
      {/* W3C Window Splitter pattern requires a focusable separator carrying
          aria-valuemin/max/now and key/pointer event handlers. An <hr>
          element cannot carry tabIndex or onKeyDown — the documented APG
          implementation is a <div role="separator">. This mirrors the
          Pill primitive's biome-ignore precedent for an a11y carve-out
          (apps/web/src/components/ui/pill.tsx). */}
      {/* biome-ignore lint/a11y/useSemanticElements: SplitPane separator is a focusable interactive control per W3C ARIA Window Splitter pattern; <hr> cannot carry tabIndex/onKeyDown. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-controls={props.ariaControls}
        aria-valuemin={Math.round(props.minRatio * 100)}
        aria-valuemax={Math.round(props.maxRatio * 100)}
        aria-valuenow={Math.round(rightPercent)}
        tabIndex={0}
        className="cursor-col-resize bg-border hover:bg-ring focus-visible:bg-ring focus-visible:outline-none"
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
      />
      <div id={props.ariaControls} className="overflow-auto p-4">
        {props.rightChildren}
      </div>
    </div>
  );
}
